// El panel de un producto del lote (ADR-2026-10-30-videos-cortos-con-el-agente), como
// `PanelDeItem` del Catálogo, en `HojaLateral`, en este orden: la idea del video (con las tomas),
// el guion (la LOCUCIÓN editable; vacía = sin voz en este video), la voz y el PROMPT editable (en
// inglés; editar lo fija). Sin el video (se mira en la tarjeta), sin el público ni el rubro, y
// sin volver el guion al escritor desde acá (eso lo pide Reescribir). Al pie, Reescribir (abre
// el agente), Rehacer (si el video ya salió) y Eliminar del lote.
// Un video creándose se puede mirar pero no tocar. Las notas por producto no se muestran: son del
// agente (`configurar_tanda` con `notas`), que las cuenta en el chat.
import { Check, ChevronDown, Loader2, Mic, RefreshCw, Trash2, VolumeX } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { HojaLateral } from '../../../../chat-atlas/ui/HojaLateral';
import { CreditBadge } from '../../../../components/CreditBadge';
import { useAperturaAnimada } from '../../../../hooks/useAperturaAnimada';
import { CREDITOS_POR_VIDEO, CREDITOS_POR_VOZ, MAX_LOCUCION, MAX_PROMPT, type ProductoDeLaTanda } from './plan';
import { textoDeEtapa } from './videosCortosApi';

/** El primer ancestro que scrollea (el cuerpo de la hoja). */
const contenedorConScroll = (el: HTMLElement): HTMLElement | null => {
  for (let p = el.parentElement; p; p = p.parentElement) {
    if (/(auto|scroll)/.test(getComputedStyle(p).overflowY)) return p;
  }
  return null;
};

/**
 * Una sola letra para todo el contenido del panel: 14 px, el mismo interlineado y el mismo negro, sin monoespaciada
 * para el prompt; los títulos de sección, 13 px seminegrita (ADR-2026-10-31-videos-cortos-panel-con-una-sola-letra).
 */
const TEXTO = 'text-[14px] leading-relaxed text-brand-black';
/** El recuadro de los campos que se editan (el guion y el prompt). La idea, que no se edita, va sobre gris. */
const CAJA = 'rounded-lg border border-black/[0.1] px-3 py-2';

export type ModoDeRehacer = 'voz' | 'video';

export interface PanelDeVideoProps {
  /** `false` mientras sale: la hoja anima la salida antes de desmontarse. Default true. */
  abierta?: boolean;
  producto: ProductoDeLaTanda;
  nombreDeVoz: string;
  generando: boolean;
  onEditarGuion: (parcial: { prompt?: string; locucion?: string }) => void;
  onElegirVoz: () => void;
  /** Qué conviene rehacer según lo editado desde que salió el video: sólo la voz o el video entero. */
  rehacerSugerido: ModoDeRehacer;
  /** El video guardó su versión sin voz: se le puede rehacer sólo la voz. */
  puedeRehacerLaVoz: boolean;
  onRehacer: (modo: ModoDeRehacer) => void;
  onReescribirConElAgente: () => void;
  onQuitar: () => void;
  onCerrar: () => void;
}

export const PanelDeVideo: React.FC<PanelDeVideoProps> = ({ abierta = true, producto, nombreDeVoz, generando, rehacerSugerido, puedeRehacerLaVoz, onEditarGuion, onElegirVoz, onRehacer, onReescribirConElAgente, onQuitar, onCerrar }) => {
  const { guion, estado } = producto;
  const enVuelo = estado === 'en_cola' || estado === 'generando';
  // Borradores locales: el guion se confirma al salir del campo. Si cada tecla fuera al plan, cada
  // tecla lo fijaría y lo guardaría (y el textarea perdería el cursor al normalizar).
  const [locucion, setLocucion] = useState(guion?.locucion?.texto ?? '');
  const [prompt, setPrompt] = useState(guion?.prompt ?? '');
  // Un borrador que la persona no tocó sigue al guion: si el panel se abrió mientras el escritor trabajaba (o el
  // asistente lo reescribe con el panel abierto), muestra el nuevo. Y salir de un campo sin tocarlo no guarda nada: un
  // borrador vacío de antes pisaba la locución recién escrita y la fijaba (Greptile #233).
  const locucionTocada = useRef(false);
  const promptTocado = useRef(false);
  useEffect(() => {
    if (!locucionTocada.current) setLocucion(guion?.locucion?.texto ?? '');
  }, [guion?.locucion?.texto]);
  useEffect(() => {
    if (!promptTocado.current) setPrompt(guion?.prompt ?? '');
  }, [guion?.prompt]);
  // El prompt se ajusta al largo del texto, entero: es lo último del panel, así que scrollea la
  // hoja y no el campo (un tope dejaba una barra de scroll adentro de otra).
  const refDelPrompt = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = refDelPrompt.current;
    if (!el) return;
    // Medir achica el campo un instante: el scroll de la hoja se guarda y se repone, si no saltaba
    // al escribir con el campo más alto que la pantalla.
    const hoja = contenedorConScroll(el);
    const scroll = hoja?.scrollTop ?? 0;
    el.style.height = 'auto';
    // scrollHeight viene redondeado hacia abajo y las líneas miden fracciones de px: sin un
    // poco de aire sobra menos de 1 px y aparece una barra de scroll vacía.
    el.style.height = `${el.scrollHeight + 6}px`;
    if (hoja) hoja.scrollTop = scroll;
  }, [prompt]);
  const confirmarLocucion = () => {
    if (!locucionTocada.current) return;
    locucionTocada.current = false;
    if ((guion?.locucion?.texto ?? '') !== locucion.trim()) onEditarGuion({ locucion: locucion.trim() });
  };
  const confirmarPrompt = () => {
    if (!promptTocado.current) return;
    promptTocado.current = false;
    if ((guion?.prompt ?? '') !== prompt.trim() && prompt.trim()) onEditarGuion({ prompt: prompt.trim() });
  };
  const quitar = () => {
    if (estado === 'listo' && !window.confirm(`¿Eliminar ${producto.nombre} del lote? El video queda en Creaciones.`)) return;
    onQuitar();
  };
  const puedeRehacer = (estado === 'listo' || estado === 'fallo' || estado === 'cancelado') && !!guion && !generando;
  const sinVoz = nombreDeVoz === 'Sin voz';

  // Rehacer: con el video listo y su versión sin voz guardada, el botón lleva un chevron para elegir entre sólo la voz
  // (2 créditos: la voz y los subtítulos sobre el mismo video) y el video completo (ADR-2026-10-31-videos-cortos-
  // rehacer-solo-la-voz). Arranca en lo que conviene según lo editado; elegir en el menú no lanza nada.
  const conEleccion = estado === 'listo' && puedeRehacerLaVoz;
  const [modo, setModo] = useState<ModoDeRehacer>(rehacerSugerido);
  useEffect(() => setModo(rehacerSugerido), [rehacerSugerido, producto.varianteId]);
  const modoEfectivo: ModoDeRehacer = conEleccion ? modo : 'video';
  const [menuAbierto, setMenuAbierto] = useState(false);
  const menu = useAperturaAnimada(menuAbierto, 150);
  const refDelRehacer = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuAbierto) return;
    const afuera = (e: PointerEvent) => {
      if (!refDelRehacer.current?.contains(e.target as Node)) setMenuAbierto(false);
    };
    const escape = (e: KeyboardEvent) => e.key === 'Escape' && setMenuAbierto(false);
    document.addEventListener('pointerdown', afuera);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', afuera);
      document.removeEventListener('keydown', escape);
    };
  }, [menuAbierto]);
  const opciones: Array<{
    modo: ModoDeRehacer;
    titulo: string;
    bajada: string;
    creditos: number;
  }> = [
    {
      modo: 'voz',
      titulo: 'Rehacer sólo la voz',
      bajada: 'La voz y los subtítulos, sobre el mismo video.',
      creditos: CREDITOS_POR_VOZ,
    },
    {
      modo: 'video',
      titulo: 'Rehacer el video completo',
      bajada: 'Se vuelve a filmar con el guion.',
      creditos: CREDITOS_POR_VIDEO,
    },
  ];

  // Con Rehacer, el pie va en dos filas: Eliminar y Reescribir arriba y Rehacer a lo ancho abajo. En una fila no
  // entraban (la hoja deja ~380 px y los tres botones con el chevron y los créditos suman más de 420): el botón se
  // apretaba y el distintivo de créditos quedaba cortado.
  const secundarios = (
    <>
      <button
        type="button"
        onClick={quitar}
        disabled={enVuelo}
        title={`Eliminar ${producto.nombre} del lote`}
        className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md py-2 px-3 text-[14px] font-semibold text-[#B42318] hover:bg-[#FFF5F4] disabled:opacity-40"
      >
        <Trash2 className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
        Eliminar
      </button>
      <button
        type="button"
        onClick={onReescribirConElAgente}
        disabled={enVuelo}
        title="Pedirle al asistente que reescriba este guion"
        className="whitespace-nowrap rounded-md py-2 px-4 text-[14px] font-semibold text-brand-primary hover:bg-[rgb(var(--brand-primary-rgb)_/_0.095)] disabled:opacity-40"
      >
        Reescribir
      </button>
    </>
  );
  const pie = !puedeRehacer ? (
    <div className="flex items-center gap-2">{secundarios}</div>
  ) : (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">{secundarios}</div>
      <div ref={refDelRehacer} className="relative">
        <div className="flex w-full items-stretch rounded-md bg-brand-primary text-white">
          <button
            type="button"
            onClick={() => {
              setMenuAbierto(false);
              onRehacer(modoEfectivo);
            }}
            className={`inline-flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-l-md py-2.5 pl-4 text-[14px] font-semibold transition-colors hover:bg-brand-primary-hover ${conEleccion ? 'pr-3' : 'rounded-r-md pr-4'}`}
          >
            {modoEfectivo === 'voz' ? <Mic className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" /> : <RefreshCw className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />}
            {modoEfectivo === 'voz' ? 'Rehacer voz' : estado === 'listo' ? 'Rehacer' : 'Crear de nuevo'}
            <CreditBadge credits={modoEfectivo === 'voz' ? CREDITOS_POR_VOZ : CREDITOS_POR_VIDEO} tone="on-primary" size="sm" />
          </button>
          {conEleccion && (
            <button
              type="button"
              onClick={() => setMenuAbierto((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={menuAbierto}
              aria-label="Elegir qué rehacer"
              className="grid shrink-0 place-items-center rounded-r-md border-l border-white/25 px-3 transition-colors hover:bg-brand-primary-hover"
            >
              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${menuAbierto ? 'rotate-180' : ''}`} strokeWidth={2.25} aria-hidden="true" />
            </button>
          )}
        </div>
        {menu.montado && (
          <div
            role="menu"
            className={`absolute bottom-full right-0 z-10 mb-2 w-[280px] rounded-lg bg-white p-1 shadow-xl ring-1 ring-black/[0.08] transition-[opacity,transform,filter] ease-out ${
              menu.visible ? 'opacity-100 duration-300 motion-safe:translate-y-0 motion-safe:blur-0' : 'opacity-0 duration-150 motion-safe:translate-y-3 motion-safe:blur-[4px]'
            }`}
          >
            {opciones.map((o) => (
              <button
                key={o.modo}
                type="button"
                role="menuitemradio"
                aria-checked={modo === o.modo}
                onClick={() => {
                  setModo(o.modo);
                  setMenuAbierto(false);
                }}
                className="flex w-full items-start gap-2.5 rounded-md px-3 py-2.5 text-left transition-colors hover:bg-black/[0.04]"
              >
                <span className="mt-0.5 grid h-4 w-4 shrink-0 place-items-center text-brand-primary">{modo === o.modo && <Check className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" />}</span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[14px] font-semibold text-brand-black">{o.titulo}</span>
                  <span className="block text-[12.5px] leading-snug text-[#3C3C43]/60">{o.bajada}</span>
                </span>
                <CreditBadge credits={o.creditos} tone="subtle" size="sm" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // La voz va entre el guion (lo que dice) y el prompt; sin guion, sola.
  const voz = (
    <div>
      <p className="mb-1.5 text-[13px] font-semibold text-brand-black">Voz</p>
      <button
        type="button"
        onClick={onElegirVoz}
        disabled={enVuelo}
        className="flex w-full items-center justify-between gap-3 rounded-md border border-black/[0.1] bg-white px-3 py-2.5 text-left transition-colors hover:bg-black/[0.02] disabled:opacity-60"
      >
        <span className="flex min-w-0 items-center gap-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-sm bg-[rgb(var(--brand-primary-rgb)_/_0.1)] text-brand-primary">
            {sinVoz ? <VolumeX className="h-4 w-4" aria-hidden="true" /> : <Mic className="h-4 w-4" aria-hidden="true" />}
          </span>
          <span className="min-w-0">
            <span className="block text-[12.5px] leading-tight text-[#3C3C43]/50">{producto.voz === undefined ? 'La voz del lote' : 'Voz de este video'}</span>
            <span className="block truncate text-[14px] leading-snug text-brand-black">{nombreDeVoz}</span>
          </span>
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 -rotate-90 text-[rgb(var(--brand-black-rgb)_/_0.4)]" aria-hidden="true" />
      </button>
    </div>
  );

  return (
    <HojaLateral
      abierta={abierta}
      titulo={producto.nombre}
      bajada={enVuelo ? textoDeEtapa({ estado, etapa: producto.etapa ?? null }) : undefined}
      onCerrar={onCerrar}
      pie={pie}
      sinFondo
      altoMobile="calc(100dvh - max(env(safe-area-inset-top), 12px))"
    >
      <div className="flex flex-col gap-4 px-1 pb-2">
        {enVuelo && (
          <p className="inline-flex items-center gap-2 text-[14px] text-brand-primary">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Creando el video… el guion ya no se cambia.
          </p>
        )}
        {(estado === 'fallo' || estado === 'cancelado' || producto.motivo) && (
          <p className="rounded-lg bg-[#FFF5F4] px-3 py-2 text-[14px] leading-relaxed text-[#B42318]">{producto.motivo ?? (estado === 'cancelado' ? 'Se canceló antes de arrancar.' : 'No salió.')}</p>
        )}
        {guion ? (
          <>
            {(guion.idea || guion.escenas.length > 0) && (
              <div>
                <p className="mb-1.5 text-[13px] font-semibold text-brand-black">Idea del video</p>
                {/* Con fondo gris y sin borde, a diferencia del guion y el prompt: no se edita. */}
                <div className={`rounded-lg bg-[#F5F6FA] px-3 py-2 ${TEXTO}`}>
                  {guion.idea && <p>{guion.idea}</p>}
                  <ol className={`list-decimal space-y-1 pl-5 text-[#3C3C43]/75 ${guion.idea ? 'mt-2' : ''}`}>
                    {guion.escenas.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ol>
                </div>
              </div>
            )}
            <div>
              <p className="mb-1.5 text-[13px] font-semibold text-brand-black">Guion{producto.fijado ? ' · editado' : ''}</p>
              <textarea
                value={locucion}
                onChange={(e) => {
                  locucionTocada.current = true;
                  setLocucion(e.target.value);
                }}
                onBlur={confirmarLocucion}
                disabled={enVuelo}
                rows={4}
                maxLength={MAX_LOCUCION}
                placeholder={sinVoz ? 'Este video va sin voz: sólo música.' : 'Lo que dice la voz. Vacío = este video sin voz.'}
                className={`w-full resize-y ${CAJA} ${TEXTO} disabled:opacity-60`}
              />
              {!sinVoz && !locucion.trim() && <p className="mt-1 text-[12.5px] text-[#B42318]">Tiene voz y no tiene locución: escribila o sacale la voz.</p>}
            </div>
            {voz}
            <div>
              <p className="mb-1.5 text-[13px] font-semibold text-brand-black">Prompt del video</p>
              <textarea
                ref={refDelPrompt}
                value={prompt}
                onChange={(e) => {
                  promptTocado.current = true;
                  setPrompt(e.target.value);
                }}
                onBlur={confirmarPrompt}
                disabled={enVuelo}
                rows={4}
                maxLength={MAX_PROMPT}
                className={`w-full resize-none overflow-y-hidden ${CAJA} ${TEXTO} disabled:opacity-60`}
              />
            </div>
          </>
        ) : (
          <>
            {voz}
            <p className="rounded-lg bg-[#F5F6FA] px-3 py-2 text-[14px] leading-relaxed text-[#3C3C43]/60">Todavía sin guion: el asistente lo escribe, o pedíselo con Reescribir.</p>
          </>
        )}
      </div>
    </HojaLateral>
  );
};
